import type { StoreApi } from 'zustand';
import type { PlaybackState } from '../playbackState';
import { clamp, syncFocusFromPlayhead } from '../playbackSync';
import { useSessionUiStore } from '../useSessionUiStore';

export function createPlaybackSlice(
  set: StoreApi<PlaybackState>['setState'],
  get: StoreApi<PlaybackState>['getState'],
): PlaybackState {
  return {
    playbackBounds: null,
    playheadMs: 0,
    isPlaying: false,
    playbackRate: 1,
    focusedTimestamp: null,
    focusedTimeRange: null,

    initPlaybackSession: (sessionId, min, max) => {
      const prev = get().playbackBounds;
      if (prev?.sessionId === sessionId && prev.min === min && prev.max === max) return;
      const playheadMs = min;
      useSessionUiStore.getState().resetListFiltersForNewPlaybackSession();
      set({
        playbackBounds: { sessionId, min, max },
        playheadMs,
        isPlaying: false,
        ...syncFocusFromPlayhead(playheadMs),
      });
    },

    setPlayheadMs: (ms, opts) => {
      const b = get().playbackBounds;
      if (!b) return;
      const c = clamp(ms, b.min, b.max);
      set({
        playheadMs: c,
        ...syncFocusFromPlayhead(c),
        ...(opts?.pause === true ? { isPlaying: false } : {}),
      });
    },

    advancePlayheadWallMs: (wallDeltaMs) => {
      const s = get();
      const b = s.playbackBounds;
      if (!b || !s.isPlaying) return;
      const delta = wallDeltaMs * s.playbackRate;
      const next = s.playheadMs + delta;
      if (next >= b.max) {
        set({
          playheadMs: b.max,
          isPlaying: false,
          ...syncFocusFromPlayhead(b.max),
        });
        return;
      }
      set({
        playheadMs: next,
        ...syncFocusFromPlayhead(next),
      });
    },

    setIsPlaying: (isPlaying) => set({ isPlaying }),

    togglePlayback: () => {
      const s = get();
      const b = s.playbackBounds;
      if (!b) return;
      if (s.isPlaying) {
        set({ isPlaying: false });
        return;
      }
      if (s.playheadMs >= b.max - 0.5) {
        const playheadMs = b.min;
        set({ isPlaying: true, playheadMs, ...syncFocusFromPlayhead(playheadMs) });
        return;
      }
      set({ isPlaying: true });
    },

    resetPlayback: () => {
      const b = get().playbackBounds;
      if (!b) return;
      const playheadMs = b.min;
      set({
        isPlaying: false,
        playheadMs,
        ...syncFocusFromPlayhead(playheadMs),
      });
    },

    stepPlaybackLandmarks: (sortedLandmarks, direction) => {
      const s = get();
      const b = s.playbackBounds;
      if (!b || sortedLandmarks.length === 0) return;
      const p = s.playheadMs;
      const eps = 1.5;
      if (direction > 0) {
        const next = sortedLandmarks.find((x) => x > p + eps);
        const playheadMs = next !== undefined ? next : b.max;
        set({ isPlaying: false, playheadMs, ...syncFocusFromPlayhead(playheadMs) });
      } else {
        const prev = [...sortedLandmarks].reverse().find((x) => x < p - eps);
        const playheadMs = prev !== undefined ? prev : b.min;
        set({ isPlaying: false, playheadMs, ...syncFocusFromPlayhead(playheadMs) });
      }
    },

    setPlaybackRate: (playbackRate) => set({ playbackRate: Math.max(0.25, Math.min(8, playbackRate)) }),
  };
}
